import { useId } from 'react';
import type { ZodError } from 'zod';

import { Field, Input } from '@/components/ui';

import { useFieldContext } from './contexts';

const firstError = (errors: ZodError[]) => errors.find((e) => e !== undefined)?.message;

interface DatePickerProps {
  label?: string;
  helperText?: string;
  required?: boolean;
  min?: string;
  max?: string;
}

/**
 * Lightweight native-input date picker. We dropped @mui/x-date-pickers in
 * Phase 8; if you need the full popup-calendar UX, swap this for
 * `react-day-picker` here without touching call sites.
 */
export function DatePicker({ label, helperText, required, min, max }: DatePickerProps) {
  const reactId = useId();
  const inputId = `dp-${reactId}`;
  const field = useFieldContext<Date | undefined>();
  const touched = field.state.meta.isTouched;
  const errMsg = touched ? firstError(field.state.meta.errors) : undefined;

  const value = toIsoDateInput(field.state.value);

  return (
    <Field
      label={label}
      required={required}
      error={errMsg}
      hint={!errMsg ? helperText : undefined}
      htmlFor={inputId}
    >
      <Input
        id={inputId}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            field.handleChange(undefined);
            return;
          }
          const d = new Date(v);
          field.handleChange(Number.isNaN(d.getTime()) ? undefined : d);
        }}
        onBlur={field.handleBlur}
        error={!!errMsg}
        required={required}
      />
    </Field>
  );
}

function toIsoDateInput(d: Date | string | undefined): string {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}
